import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IsotipoRedondo from 'assets/images/logos/IsotipoRedondo.svg';
import LoadingOverlay from 'components/UI/LoadingOverlay';
import useQuery from 'hooks/useQuery';
import type React from 'react';
import { type FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePasswordResetMutation } from 'services/api/user.api';
import {
  LIGHT_GREY,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  STATUS_ERROR,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';
import validatePassword, { getPasswordErrorMessage } from 'utils/validations/validatePassword';

const FONT = 'Jost, sans-serif';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: SURFACE_CONTAINER_LOW,
    borderRadius: RADIUS_ITEM,
    fontFamily: FONT,
    fontSize: '0.95rem',
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { borderColor: alpha(NAVY, 0.15) },
    '&.Mui-focused fieldset': { borderColor: alpha(NAVY, 0.3), borderWidth: '1.5px' },
    '&.Mui-focused': { bgcolor: LIGHT_GREY },
  },
  '& .MuiInputLabel-root': { fontFamily: FONT, color: alpha(NAVY, 0.5) },
  '& .MuiInputLabel-root.Mui-focused': { color: NAVY },
  '& .MuiFormHelperText-root': { fontFamily: FONT },
};

const PasswordReset: FC = () => {
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [passwordConfirmError, setPasswordConfirmError] = useState(false);

  const [passwordReset, { isLoading, isError, error, isSuccess }] = usePasswordResetMutation();

  const query = useQuery();
  const navigate = useNavigate();
  const userEmail = query.get('userEmail') || '';

  const passwordVerify = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    setPasswordError(!!event.target.value && !validatePassword(event.target.value, userEmail));
  };

  const passwordConfirmVerify = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordConfirm(event.target.value);
    setPasswordConfirmError(!!event.target.value && password !== event.target.value);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Submit is always enabled (design §21.7): validate on click and surface what to fix.
    const invalidPassword = !validatePassword(password, userEmail);
    const mismatch = password !== passwordConfirm;
    setPasswordError(invalidPassword);
    // Flag the confirm field when empty too, else submit blocks with no visible reason.
    setPasswordConfirmError(!passwordConfirm || mismatch);
    if (invalidPassword || mismatch || !passwordConfirm) return;
    passwordReset({ password, email: userEmail, token: query.get('token') || '' }).catch(() => {});
  };

  useEffect(() => {
    if (isSuccess) navigate('/sign-in?reset=1');
  }, [isSuccess, navigate]);

  // Surface the backend message when it's user-actionable (expired token, policy); else generic.
  const errorMessage =
    (isError && error && 'data' in error && typeof error.data === 'string' && error.data) ||
    'No pudimos crear tu contraseña. El enlace puede haber expirado — solicita uno nuevo desde "Olvidé mi contraseña".';

  return (
    <>
      <LoadingOverlay visible={isLoading} />

      <Box sx={{ display: 'flex', minHeight: '100vh', fontFamily: FONT, bgcolor: NAVY }}>
        {/* ── Left hero panel ── */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            width: { md: '46%', lg: '50%' },
            bgcolor: '#fff',
            flexDirection: 'column',
            justifyContent: 'space-between',
            p: { md: 5, lg: 7 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              flex: 1,
              textAlign: 'center',
              px: { md: 2, lg: 6 },
            }}
          >
            <img
              src="/logo.webp"
              alt="Relif"
              height="80"
              style={{ objectFit: 'contain', display: 'block', marginBottom: '32px' }}
            />
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: 700,
                fontSize: 'clamp(2rem, 2.8vw, 3.2rem)',
                lineHeight: 1.2,
                color: NAVY,
              }}
            >
              Un último paso para activar tu cuenta.
            </Typography>
          </Box>

          <Typography sx={{ fontFamily: FONT, fontSize: '0.75rem', color: alpha(NAVY, 0.25) }}>
            © {new Date().getFullYear()} Relif · Todos los derechos reservados
          </Typography>
        </Box>

        {/* ── Right form panel ── */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 3, sm: 5 },
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 440,
              bgcolor: SURFACE_CONTAINER_LOWEST,
              borderRadius: RADIUS_CARD,
              p: { xs: 4, sm: 5 },
              boxShadow: '0px 24px 64px rgba(21, 24, 58, 0.1)',
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: -2 }}>
              <img src={IsotipoRedondo} alt="Relif" height="48" style={{ objectFit: 'contain' }} />
              <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.75rem', color: NAVY, lineHeight: 1.2 }}>
                Crea tu contraseña
              </Typography>
            </Box>

            <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', color: alpha(NAVY, 0.45) }}>
              Elige una contraseña segura para acceder a tu espacio de trabajo.
            </Typography>

            {isError && (
              <Box
                sx={{
                  bgcolor: alpha(STATUS_ERROR, 0.06),
                  borderRadius: RADIUS_ITEM,
                  p: 1.5,
                  fontFamily: FONT,
                  fontSize: '0.85rem',
                  color: STATUS_ERROR,
                }}
              >
                {errorMessage}
              </Box>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                id="password"
                name="password"
                autoComplete="new-password"
                fullWidth
                label="Nueva contraseña"
                type="password"
                onChange={passwordVerify}
                error={passwordError}
                helperText={
                  passwordError
                    ? getPasswordErrorMessage(password, userEmail)
                    : 'Mínimo 12 caracteres, con mayúscula, minúscula, número y un carácter especial.'
                }
                autoFocus
                sx={fieldSx}
              />
              <TextField
                id="passwordConfirm"
                name="passwordConfirm"
                fullWidth
                label="Confirmar contraseña"
                type="password"
                onChange={passwordConfirmVerify}
                error={passwordConfirmError}
                helperText={
                  passwordConfirmError
                    ? passwordConfirm
                      ? 'Las contraseñas no coinciden'
                      : 'Confirma tu contraseña'
                    : ''
                }
                sx={fieldSx}
              />

              <Button
                type="submit"
                fullWidth
                disabled={isLoading}
                sx={{
                  bgcolor: YELLOW,
                  color: NAVY,
                  borderRadius: RADIUS_COIN,
                  py: 1.5,
                  fontFamily: FONT,
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: `0 4px 8px ${alpha(YELLOW, 0.25)}`,
                  mt: 0.5,
                  '&:hover': { bgcolor: alpha(YELLOW, 0.85) },
                  '&.Mui-disabled': { bgcolor: alpha(YELLOW, 0.5), color: alpha(NAVY, 0.5) },
                  transition: 'all 0.46s cubic-bezier(0.25, 0.8, 0.25, 1)',
                }}
              >
                {isLoading ? 'Guardando…' : 'Guardar contraseña'}
              </Button>
            </Box>

            <Button
              onClick={() => navigate('/sign-in')}
              sx={{
                fontFamily: FONT,
                color: alpha(NAVY, 0.4),
                textTransform: 'none',
                fontSize: '0.875rem',
                borderRadius: RADIUS_ITEM,
                '&:hover': { bgcolor: alpha(NAVY, 0.05), color: alpha(NAVY, 0.7) },
                transition: 'all 0.15s ease-in-out',
              }}
            >
              Volver a iniciar sesión
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default PasswordReset;
